import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from "@air/primitive-button";
import { Modal, ModalCloseButton, ModalTitle } from "@air/primitive-modal";
import differenceBy from "lodash/differenceBy";
import intersectionBy from "lodash/intersectionBy";
import map from "lodash/map";
import pluralize from "pluralize";
import { memo, useCallback, useMemo, useState } from "react";
import { Select } from "~/components/Select/Select";
import { BULK_EDIT_MULTI_SELECT_MODAL, CUSTOM_FIELD_SELECT_COMPONENT } from "~/constants/testIDs";
import { getCustomFieldSelectOption } from "~/utils/CustomFields";
export const BulkEditMultiSelectCustomFieldModal = /*#__PURE__*/ memo((param)=>{
    let { field, assetIds, boardIds, initialValues: _initialValues, onClose, cfOptions, onSave } = param;
    const fieldValues = useMemo(()=>(cfOptions === null || cfOptions === void 0 ? void 0 : cfOptions.data) || [], [
        cfOptions === null || cfOptions === void 0 ? void 0 : cfOptions.data
    ]);
    const initialValues = intersectionBy(..._initialValues.map((cf)=>cf.values || []), "id").map(getCustomFieldSelectOption);
    const [values, setValues] = useState(initialValues);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isTouched, setIsTouched] = useState(false);
    const selectOptions = useMemo(()=>{
        if (!cfOptions) return [];
        return cfOptions.data.map((option)=>getCustomFieldSelectOption(option));
    }, [
        cfOptions
    ]);
    const onSelectionChange = useCallback((param)=>{
        let { chips } = param;
        setIsTouched(true);
        setValues(chips);
    }, []);
    const onConfirm = useCallback(async ()=>{
        setIsSubmitting(true);
        const valueIdsToAdd = map(differenceBy(values, initialValues, "value"), "value");
        const multiValueIdsToRemove = map(differenceBy(initialValues, values, "value"), "value");
        const multiValuesToAdd = fieldValues.filter((v)=>valueIdsToAdd.includes(v.id));
        await onSave({
            assetIds,
            boardIds,
            baseCustomField: field,
            multiValueIdsToRemove,
            multiValuesToAdd
        });
        onClose();
    }, [
        values,
        initialValues,
        fieldValues,
        onSave,
        assetIds,
        boardIds,
        field,
        onClose
    ]);
    const placeholder = useMemo(()=>{
        if (isTouched) return "None";
        return "Find an option";
    }, [
        isTouched
    ]);
    return /*#__PURE__*/ _jsx(Modal, {
        isOpen: true,
        "data-testid": BULK_EDIT_MULTI_SELECT_MODAL,
        onDismiss: onClose,
        children: /*#__PURE__*/ _jsxs("div", {
            className: "flex flex-col gap-6",
            children: [
                /*#__PURE__*/ _jsxs("header", {
                    className: "flex justify-between gap-4",
                    children: [
                        /*#__PURE__*/ _jsxs(ModalTitle, {
                            "data-testid": "MODAL_LABEL",
                            children: [
                                "Edit ",
                                field.name
                            ]
                        }),
                        /*#__PURE__*/ _jsx(ModalCloseButton, {
                            className: "shrink-0",
                            onClick: onClose
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("p", {
                    className: "text-16 text-grey-11",
                    children: [
                        "Choose or remove a value or multiple values from the list below to apply to the",
                        " ",
                        pluralize("item", assetIds.length + boardIds.length, true),
                        " you’ve selected."
                    ]
                }),
                /*#__PURE__*/ _jsx(Select, {
                    autoFocus: true,
                    "data-testid": "".concat(CUSTOM_FIELD_SELECT_COMPONENT, "-").concat(field.name),
                    options: selectOptions,
                    placeholder: placeholder,
                    selectedOptions: values,
                    isClearable: true,
                    isSearchable: true,
                    className: "w-full",
                    maxDropdownHeight: 300,
                    onSelectionChange: onSelectionChange,
                    isSingleSelect: false,
                    size: "extra-large",
                    isLoading: !cfOptions
                }),
                /*#__PURE__*/ _jsxs("footer", {
                    className: "flex justify-end gap-3",
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            appearance: "ghost",
                            color: "grey",
                            onClick: onClose,
                            size: "large",
                            children: "Cancel"
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            "data-testid": "BULK_EDIT_MULTI_SELECT_CUSTOM_FIELD_MODAL_SAVE",
                            isLoading: isSubmitting,
                            disabled: !isTouched || isSubmitting,
                            appearance: "filled",
                            color: "blue",
                            size: "large",
                            onClick: onConfirm,
                            children: "Save"
                        })
                    ]
                })
            ]
        })
    });
});
BulkEditMultiSelectCustomFieldModal.displayName = "BulkEditMultiSelectCustomFieldModal";
